import { useField } from 'formik';
import { ChangeEvent } from 'react';
import { TextField, Typography, TextFieldProps } from '@mui/material';

import { controlError } from 'utils/error';
import { questionLabelText } from 'utils/question-label';

export interface InputFieldProps extends Omit<TextFieldProps, 'name'> {
  name: string;
  label?: string;
  pattern?: string;
  displayValue?: unknown;
  controlErrorLabel?: boolean;
  onChange?: TextFieldProps['onChange'];
  mapValue?: (value: unknown) => unknown;
  mapSelectValue?: (value: string) => string;
}

export const InputField = ({
  name,
  type,
  label,
  pattern,
  onChange,
  mapValue,
  inputMode,
  slotProps,
  placeholder,
  displayValue,
  mapSelectValue,
  ...props
}: InputFieldProps) => {
  const [field, meta, helpers] = useField(name);

  const errorText = controlError(meta, name, label);

  const isNumber = type === 'number';

  const inputSlotProps: InputFieldProps['slotProps'] = {
    ...slotProps,
    ...(slotProps?.select && {
      select: {
        displayEmpty: true,
        renderValue: (selected) => {
          if (selected == null) {
            return (
              <Typography component="span" sx={{ color: 'text.disabled' }}>
                {placeholder}
              </Typography>
            );
          }
          if (typeof selected === 'string') {
            if (selected.length === 0) {
              return (
                <Typography
                  component="span"
                  sx={{
                    color: 'text.disabled',
                  }}
                >
                  {placeholder}
                </Typography>
              );
            }
            return mapSelectValue ? mapSelectValue(selected) : selected;
          }
          if (Array.isArray(selected)) {
            if (selected.length === 0) {
              return (
                <Typography
                  component="span"
                  sx={{
                    color: 'text.disabled',
                  }}
                >
                  {placeholder}
                </Typography>
              );
            }
            let items = selected;
            if (mapSelectValue) {
              items = selected.map((value) => mapSelectValue(value));
            }
            return items.join(', ');
          }
          return mapSelectValue
            ? mapSelectValue(selected as string)
            : (selected as string);
        },
        ...slotProps.select,
      },
    }),
  };

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (mapValue) {
      helpers.setValue(mapValue(event.target.value), true);
      onChange?.(event);
      return;
    }

    if (isNumber) {
      const regex = new RegExp(pattern ?? '[^0-9]', 'g');
      const value = event.target.value.replace(regex, '');
      if (value.length) {
        const number = Number(value);
        helpers.setValue(
          Number.isNaN(number) || value.endsWith('.') ? value : number,
          true
        );
        onChange?.(event);
        return;
      }
    }

    field.onChange(event);
    onChange?.(event);
  };

  return (
    <TextField
      {...props}
      {...field}
      error={!!errorText}
      helperText={errorText}
      onChange={handleChange}
      placeholder={placeholder}
      slotProps={inputSlotProps}
      type={isNumber ? 'text' : type}
      value={displayValue ?? field.value ?? ''}
      inputMode={isNumber ? 'numeric' : inputMode}
      label={label ? questionLabelText(label) : label}
    />
  );
};
